import React, { useState, useCallback, useEffect } from 'react';
import OverlayLayer from './OverlayLayer.jsx';
import { KIND, pointsRequired, recompute, formatValue } from '../lib/measurements.js';

// Measurement rendering + click-to-create. Emits onCreate(payload) when a
// measurement is complete; parent persists via API.
//
// Active kind controls what's being drawn:
//   null | 'distance' | 'angle' | 'cobb' | 'ellipse' | 'calibration'
export default function MeasurementLayer({
  measurements = [],
  activeKind = null,
  mmPerPx = null,
  imageWidth, imageHeight,
  canvasWidth, canvasHeight,
  panX, panY, zoom, rotation,
  onCreate,
  onCalibrationNeedValue, // (points) => opens a dialog for user to enter known mm
}) {
  const [partial, setPartial] = useState(null); // { kind, points: [[x,y]] }

  // Reset partial if tool changes
  useEffect(() => { setPartial(null); }, [activeKind]);

  const onPointerDown = useCallback((pt) => {
    if (!activeKind) return;
    const pts = partial && partial.kind === activeKind ? [...partial.points, pt] : [pt];
    const next = { kind: activeKind, points: pts };
    const need = pointsRequired(activeKind);
    if (pts.length >= need && need !== Infinity) {
      if (activeKind === KIND.CALIBRATION) {
        // Ask parent to prompt for known length in mm
        onCalibrationNeedValue?.(pts);
        setPartial(null);
        return;
      }
      const derived = recompute(next, mmPerPx);
      onCreate?.({ ...next, ...derived });
      setPartial(null);
    } else {
      setPartial(next);
    }
  }, [activeKind, partial, mmPerPx, onCreate, onCalibrationNeedValue]);

  const interactive = !!activeKind;
  const overlayProps = { imageWidth, imageHeight, canvasWidth, canvasHeight, panX, panY, zoom, rotation };

  // Strokewidth in image pixels, scaled so it appears ~1.5px on screen
  const sw = Math.max(0.5, 1.5 / Math.max(0.1, (Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight) * zoom)));
  const textSize = Math.max(8, 14 / Math.max(0.1, (Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight) * zoom)));

  return (
    <OverlayLayer {...overlayProps} interactive={interactive} onPointerDown={onPointerDown}>
      {measurements.map(m => (
        <MeasurementShape key={m.id} m={m} sw={sw} textSize={textSize} color="#2dd4a0" mmPerPx={mmPerPx} />
      ))}
      {partial && <MeasurementShape m={{ ...partial, id: 'partial' }} sw={sw} textSize={textSize} color="rgba(45,212,160,0.7)" dashed mmPerPx={mmPerPx} />}
    </OverlayLayer>
  );
}

function MeasurementShape({ m, sw, textSize, color, dashed, mmPerPx }) {
  const pts = m.points || [];
  if (pts.length === 0) return null;
  const strokeDash = dashed ? '6 4' : undefined;
  const derived = recompute(m, mmPerPx);
  const labelText = formatValue({ ...m, ...derived });

  if (m.kind === KIND.DISTANCE || m.kind === KIND.CALIBRATION) {
    if (pts.length < 2) {
      return <circle cx={pts[0][0]} cy={pts[0][1]} r={sw * 2} fill={color} />;
    }
    const midX = (pts[0][0] + pts[1][0]) / 2;
    const midY = (pts[0][1] + pts[1][1]) / 2;
    return (
      <g>
        <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke={color} strokeWidth={sw} strokeDasharray={strokeDash} />
        <circle cx={pts[0][0]} cy={pts[0][1]} r={sw * 2} fill={color} />
        <circle cx={pts[1][0]} cy={pts[1][1]} r={sw * 2} fill={color} />
        <text x={midX} y={midY - sw * 4} fill={color} fontSize={textSize} fontFamily="'JetBrains Mono', monospace" paintOrder="stroke" stroke="rgba(0,0,0,0.6)" strokeWidth={sw * 0.5}>
          {labelText}
        </text>
      </g>
    );
  }

  if (m.kind === KIND.ANGLE) {
    if (pts.length < 2) {
      return <circle cx={pts[0][0]} cy={pts[0][1]} r={sw * 2} fill={color} />;
    }
    return (
      <g>
        {pts.length >= 2 && <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke={color} strokeWidth={sw} strokeDasharray={strokeDash} />}
        {pts.length >= 3 && <line x1={pts[1][0]} y1={pts[1][1]} x2={pts[2][0]} y2={pts[2][1]} stroke={color} strokeWidth={sw} strokeDasharray={strokeDash} />}
        {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={sw * 2} fill={color} />)}
        {pts.length >= 3 && (
          <text x={pts[1][0] + sw * 4} y={pts[1][1] - sw * 4} fill={color} fontSize={textSize} fontFamily="'JetBrains Mono', monospace" paintOrder="stroke" stroke="rgba(0,0,0,0.6)" strokeWidth={sw * 0.5}>
            {labelText}
          </text>
        )}
      </g>
    );
  }

  if (m.kind === KIND.COBB) {
    return (
      <g>
        {pts.length >= 2 && <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke={color} strokeWidth={sw} strokeDasharray={strokeDash} />}
        {pts.length >= 4 && <line x1={pts[2][0]} y1={pts[2][1]} x2={pts[3][0]} y2={pts[3][1]} stroke={color} strokeWidth={sw} strokeDasharray={strokeDash} />}
        {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={sw * 2} fill={color} />)}
        {pts.length >= 4 && (
          <text x={(pts[1][0] + pts[2][0]) / 2} y={(pts[1][1] + pts[2][1]) / 2} fill={color} fontSize={textSize} fontFamily="'JetBrains Mono', monospace" paintOrder="stroke" stroke="rgba(0,0,0,0.6)" strokeWidth={sw * 0.5}>
            {labelText}
          </text>
        )}
      </g>
    );
  }

  if (m.kind === KIND.ELLIPSE && pts.length >= 2) {
    const cx = (pts[0][0] + pts[1][0]) / 2;
    const cy = (pts[0][1] + pts[1][1]) / 2;
    const rx = Math.abs(pts[1][0] - pts[0][0]) / 2;
    const ry = Math.abs(pts[1][1] - pts[0][1]) / 2;
    return (
      <g>
        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} stroke={color} strokeWidth={sw} fill="none" strokeDasharray={strokeDash} />
        <text x={cx} y={cy - ry - sw * 4} fill={color} fontSize={textSize} fontFamily="'JetBrains Mono', monospace" paintOrder="stroke" stroke="rgba(0,0,0,0.6)" strokeWidth={sw * 0.5}>
          {labelText}
        </text>
      </g>
    );
  }

  return null;
}
